import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  AUTH_TOKEN_KEY,
  fetchAdminTeamInfo,
  fetchSystemUserInfo,
  fetchTeamSubPrice,
  getUserTeams,
  hasCreatedOwnTeam,
  isTeamAdmin,
  isUserInTeam,
  isTeamInfoEmpty,
  isTeamSubYearly,
  needsJoinOrCreatePrompt,
  getUserPostalCodes,
  parseDisplayTeamInfo,
  postSwitchTeam,
  readTeamCountry,
  readTeamInfoId,
  showApiResultModal,
  ApiRequestError,
  type AdminTeamInfo,
  type DisplayTeamInfo,
  type SubscriptionUiStatus,
  type SystemUserInfo,
  type TeamSubPrice,
} from '@/api';
import NoTeamPrompt from '@/components/team/NoTeamPrompt';
import dayjs from 'dayjs';

type SettingNavItem = {
  icon: string;
  label: string;
  desc: string;
  path: string;
};

const accountSettingGroup = {
  title: 'Personal Account',
  items: [
    { icon: 'ri-user-settings-line', label: 'Personal Information', desc: 'Name, email, phone', path: '/me/profile' },
    { icon: 'ri-bar-chart-box-line', label: 'My Work Statistics', desc: 'Your hours and commission by date', path: '/me/statistics' },
    { icon: 'ri-wallet-3-line', label: 'My Wallet', desc: 'Balance and earnings history', path: '/me/wallet' },
    { icon: 'ri-lock-password-line', label: 'Change Password', desc: 'Last changed 30 days ago', path: '/me/password' },
    { icon: 'ri-customer-service-2-line', label: 'Help & Support', desc: 'FAQs and contact', path: '/me/support' },
  ] as SettingNavItem[],
};

const teamSettingGroup = {
  title: 'My created team',
  items: [
    { icon: 'ri-settings-3-line', label: 'Settings', desc: 'Team profile, contact, and logo', path: '/team/settings' },
    { icon: 'ri-group-line', label: 'Members', desc: 'Invite, review, and manage team members', path: '/team/members' },
    { icon: 'ri-bar-chart-box-line', label: 'Team Work Statistics', desc: 'Staff hours and commission', path: '/team/statistics' },
    { icon: 'ri-bill-line', label: 'Invoice', desc: 'Tax ID, tax rate, and bank details', path: '/team/invoice' },
    { icon: 'ri-repeat-line', label: 'Recurring Orders', desc: 'Create, edit, and stop auto-renewal rules', path: '/team/recurring' },
    { icon: 'ri-vip-diamond-line', label: 'Subscription', desc: 'Plan, seats, and billing', path: '/subscribe' },
  ] as SettingNavItem[],
};

function initialsFromName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'ME';
  return trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** 自己创建的团队信息来自 `team_info`，不再用 team_admin / role。 */
function resolveCreatedTeamInfo(profile: SystemUserInfo | null): DisplayTeamInfo | null {
  if (!profile || isTeamInfoEmpty(profile.team_info)) return null;
  return parseDisplayTeamInfo(profile.team_info);
}

/** Team 操作面板：有自己创建的团队（team_info 有数据）时显示。 */
function canShowTeamManagePanel(
  profile: SystemUserInfo | null,
  profileLoading: boolean,
): boolean {
  if (profileLoading || !profile) return false;
  return hasCreatedOwnTeam(profile);
}

function readCreatedTeamExpireAt(
  profile: SystemUserInfo | null,
  adminTeam: AdminTeamInfo | null,
): string | null {
  const fromAdmin =
    adminTeam?.expire_time?.trim() ||
    adminTeam?.subscription_info?.expire_time?.trim() ||
    '';
  if (fromAdmin) return fromAdmin;
  const teamInfo = profile?.team_info;
  if (!teamInfo || typeof teamInfo !== 'object') return null;
  const raw = teamInfo.expire_time ?? teamInfo.expireTime;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function createdTeamSubStatus(
  expireAt: string | null,
): SubscriptionUiStatus {
  if (!expireAt) return 'none';
  const exp = new Date(expireAt);
  if (Number.isNaN(exp.getTime())) return 'active';
  return exp.getTime() > Date.now() ? 'active' : 'expired';
}

function isTeamOnTrial(profile: SystemUserInfo | null, adminTeam: AdminTeamInfo | null): boolean {
  if (adminTeam?.is_trial_activated === 1) return true;
  const teamInfo = profile?.team_info;
  if (!teamInfo || typeof teamInfo !== 'object') return false;
  const raw = teamInfo.is_trial_activated ?? teamInfo.isTrialActivated;
  return raw === 1 || raw === '1' || raw === true;
}

function readRecordNumber(source: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  if (!source) return null;
  for (const key of keys) {
    const raw = source[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) return Number(raw);
  }
  return null;
}

type CreatedTeamSeats = {
  used: number | null;
  limit: number | null;
  extra: number;
  base: number | null;
};

function readCreatedTeamSeats(
  profile: SystemUserInfo | null,
  adminTeam: AdminTeamInfo | null,
  price: TeamSubPrice | null,
): CreatedTeamSeats {
  const cap = adminTeam?.member_capacity ?? price?.teamCapacity ?? null;
  if (cap) {
    return {
      used: cap.currentCount,
      limit: cap.totalCapacity,
      extra: cap.extraMembers,
      base: cap.baseMembers,
    };
  }

  const teamInfo = profile?.team_info;
  const nested =
    teamInfo && typeof teamInfo.member_capacity === 'object' && teamInfo.member_capacity
      ? (teamInfo.member_capacity as Record<string, unknown>)
      : teamInfo && typeof teamInfo.memberCapacity === 'object' && teamInfo.memberCapacity
        ? (teamInfo.memberCapacity as Record<string, unknown>)
        : null;

  const extra =
    readRecordNumber(nested, ['extra_members', 'extraMembers']) ??
    readRecordNumber(teamInfo, ['extra_members', 'extraMembers']) ??
    adminTeam?.extra_members ??
    0;
  const base =
    readRecordNumber(nested, ['base_members', 'baseMembers']) ??
    readRecordNumber(teamInfo, ['base_members', 'baseMembers']) ??
    adminTeam?.base_members ??
    null;
  const used =
    readRecordNumber(nested, ['current_count', 'currentCount']) ??
    readRecordNumber(teamInfo, ['member_count', 'memberCount']) ??
    adminTeam?.member_count ??
    null;
  const limit =
    readRecordNumber(nested, ['total_capacity', 'totalCapacity']) ??
    (base != null ? base + extra : null);

  return { used, limit, extra, base };
}

function createdTeamPlanLabel(
  status: SubscriptionUiStatus,
  trial: boolean,
  yearly: boolean,
): string {
  if (status === 'none') return 'No subscription';
  if (trial) return 'Free trial';
  return yearly ? 'Yearly team plan' : 'Monthly team plan';
}

function formatTeamExpireSubtitle(
  expireAt: string | null,
  status: SubscriptionUiStatus,
): string {
  if (!expireAt) {
    return status === 'none' ? 'No expiry date' : 'Expiry date unavailable';
  }
  const d = dayjs(expireAt);
  if (!d.isValid()) return 'Expiry date unavailable';
  const date = d.format('D MMM YYYY');
  return status === 'expired' ? `Expired ${date}` : `Expires ${date}`;
}

function formatSeatSummary(seats: CreatedTeamSeats): string | null {
  if (seats.limit != null && seats.limit > 0) {
    const usedPart = seats.used != null ? `${seats.used} of ${seats.limit}` : `Up to ${seats.limit}`;
    const extraPart = seats.extra > 0 ? ` · ${seats.extra} extra` : '';
    return `${usedPart} member seats${extraPart}`;
  }
  if (seats.used != null) {
    return `${seats.used} team member${seats.used === 1 ? '' : 's'}`;
  }
  return null;
}

function resolveCreatedTeamCode(
  profile: SystemUserInfo | null,
  display: DisplayTeamInfo | null,
  adminTeam: AdminTeamInfo | null,
): string {
  const fromAdmin = adminTeam?.team_code?.trim();
  if (fromAdmin) return fromAdmin;
  const fromDisplay = display?.teamCode?.trim();
  if (fromDisplay) return fromDisplay;
  const createdId = profile ? readTeamInfoId(profile.team_info) : null;
  const own = getUserTeams(profile).find((team) =>
    createdId != null ? String(team.teamId) === createdId : team.role === 1,
  );
  return own?.teamCode?.trim() ?? '';
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

function teamSubscriptionBadge(
  status: SubscriptionUiStatus,
  trial: boolean,
): { label: string; className: string } {
  if (trial && status === 'active') {
    return { label: 'Trial', className: 'bg-amber-50 text-amber-700 border-amber-200' };
  }
  if (status === 'active') {
    return { label: 'Active', className: 'bg-green-50 text-green-700 border-green-200' };
  }
  if (status === 'expired') {
    return { label: 'Expired', className: 'bg-red-50 text-red-600 border-red-200' };
  }
  return { label: 'Inactive', className: 'bg-gray-100 text-gray-500 border-gray-200' };
}

export default function MePage() {
  const { logout, updateUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [profile, setProfile] = useState<SystemUserInfo | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [adminTeamDetail, setAdminTeamDetail] = useState<AdminTeamInfo | null>(null);
  const [teamPrice, setTeamPrice] = useState<TeamSubPrice | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!isAuthenticated || !localStorage.getItem(AUTH_TOKEN_KEY)) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);
    setProfileError('');

    try {
      const info = await fetchSystemUserInfo();
      setProfile(info);
      updateUser?.({
        name: info.doctor_name,
        email: info.email,
        sysname: info.sys_name,
        isTeamAdmin: isTeamAdmin(info),
        avatarUrl: info.avatar,
        mobile: info.mobile,
        user_id: String(info.userID),
        gender: info.gender,
        postalCode: info.postal_code?.trim() || undefined,
        postalCodes: getUserPostalCodes(info),
      });
    } catch (err) {
      setProfile(null);
      setProfileError(err instanceof ApiRequestError ? err.message : 'Failed to load profile.');
    } finally {
      setProfileLoading(false);
    }
  }, [isAuthenticated, updateUser]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const state = location.state as { refreshProfile?: boolean } | null;
    if (state?.refreshProfile) {
      void loadProfile();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, loadProfile, navigate]);

  useEffect(() => {
    if (!profile || !hasCreatedOwnTeam(profile) || !isTeamAdmin(profile)) {
      setAdminTeamDetail(null);
      setTeamPrice(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      const info = await fetchAdminTeamInfo().catch(() => null);
      const country = info?.country || readTeamCountry(profile.team_info);
      const price = await fetchTeamSubPrice({ country }).catch(() => null);
      if (cancelled) return;
      setAdminTeamDetail(info);
      setTeamPrice(price);
    })();

    return () => {
      cancelled = true;
    };
  }, [profile]);

  const showNoTeamPrompt = !profileLoading && needsJoinOrCreatePrompt(profile);
  const displayName = profile?.doctor_name?.trim() || 'My Account';
  const displayEmail = profile?.email ?? '';
  const showTeamPanel = canShowTeamManagePanel(profile, profileLoading);
  const createdTeamDisplay = showTeamPanel ? resolveCreatedTeamInfo(profile) : null;
  const personalItems = isUserInTeam(profile)
    ? accountSettingGroup.items
    : accountSettingGroup.items.filter((item) => item.path !== '/me/statistics');
  const settingGroups = showTeamPanel
    ? [teamSettingGroup, { ...accountSettingGroup, items: personalItems }]
    : [{ ...accountSettingGroup, items: personalItems }];
  const avatarUrl = profile?.avatar;
  const initials = initialsFromName(profile?.doctor_name ?? '');
  const teamInitials = initialsFromName(createdTeamDisplay?.name ?? 'T');
  const teamExpireAt = showTeamPanel ? readCreatedTeamExpireAt(profile, adminTeamDetail) : null;
  const teamSubStatus = showTeamPanel ? createdTeamSubStatus(teamExpireAt) : 'none';
  const teamTrial = showTeamPanel && isTeamOnTrial(profile, adminTeamDetail);
  const teamSubBadge = teamSubscriptionBadge(teamSubStatus, teamTrial);
  const teamYearly =
    (adminTeamDetail?.subscription_info?.months ?? 0) >= 12 || isTeamSubYearly(profile);
  const teamPlanLabel = createdTeamPlanLabel(teamSubStatus, teamTrial, teamYearly);
  const teamSeatSummary = showTeamPanel
    ? formatSeatSummary(readCreatedTeamSeats(profile, adminTeamDetail, teamPrice))
    : null;
  const teamCode = showTeamPanel
    ? resolveCreatedTeamCode(profile, createdTeamDisplay, adminTeamDetail)
    : '';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleCopyTeamCode = async () => {
    if (!teamCode) return;
    const ok = await copyTextToClipboard(teamCode);
    if (ok) {
      setCodeCopied(true);
      window.setTimeout(() => setCodeCopied(false), 1600);
    }
    showApiResultModal({
      message: ok ? 'Team code copied. Share it with people joining your team.' : 'Failed to copy team code.',
      success: ok,
    });
  };

  const handleSettingNavigate = async (path: string) => {
    const [pathname, hash] = path.split('#');
    if (!pathname) return;

    const createdTeamId = profile ? readTeamInfoId(profile.team_info) : null;
    const isCreatedTeamPath = pathname.startsWith('/team/') || pathname === '/subscribe';
    if (isCreatedTeamPath && createdTeamId) {
      const result = await postSwitchTeam(createdTeamId);
      if (result.error) {
        showApiResultModal({ message: result.message, success: false });
        return;
      }
      if (result.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, result.token);
      }
    }

    navigate(hash ? { pathname, hash: `#${hash}` } : pathname);
  };

  return (
    <div className="ui-tab-page bg-[#f8f9fa]">
      <div className="relative bg-white pt-6 pb-0 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-br from-green-600 via-green-500 to-teal-500 rounded-b-[2rem]" />

        <div className="relative px-4 flex flex-col items-center text-center pb-3">
          <button
            onClick={() => navigate('/me/avatar')}
            className="relative w-14 h-14 rounded-full bg-green-700 flex items-center justify-center shadow-md border-[3px] border-white text-white text-base font-semibold tracking-wide mb-2 mt-2 cursor-pointer overflow-hidden group"
          >
            {profileLoading ? (
              <i className="ri-loader-4-line text-lg animate-spin text-white/80" />
            ) : avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              initials
            )}
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <i className="ri-camera-line text-white text-sm" />
            </div>
          </button>
          {profileLoading ? (
            <div className="w-full max-w-[11rem] space-y-1.5 mb-1 mt-2" aria-busy="true">
              <div className="h-4 bg-gray-200 rounded-md animate-pulse mx-auto w-28" />
              <div className="h-2.5 bg-gray-100 rounded-md animate-pulse mx-auto w-36" />
            </div>
          ) : (
            <>
              <h2 className="text-gray-900 font-semibold text-base leading-tight">{displayName}</h2>
              {displayEmail ? (
                <p className="text-white-500 text-[11px] pt-1">{displayEmail}</p>
              ) : null}
            </>
          )}
          {profileError && !profileLoading ? (
            <p className="text-red-500 text-[11px] mt-2 px-2" role="alert">
              {profileError}
            </p>
          ) : null}
        </div>
      </div>

      <div className="px-4 space-y-2.5 pt-3">
        {showNoTeamPrompt && <NoTeamPrompt profile={profile} />}

        {settingGroups.map((group) => (
          <div key={group.title} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2">
              <span className="text-gray-400 text-[10px] font-medium uppercase tracking-wider">{group.title}</span>
              {group.title === 'My created team' ? (
                <span className="flex items-center gap-1 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 flex-shrink-0">
                  <i className="ri-shield-user-line text-green-600 text-[10px]" />
                  <span className="text-green-700 text-[10px] font-medium">Team Admin</span>
                </span>
              ) : null}
            </div>
            {group.title === 'My created team' && createdTeamDisplay ? (
              <div className="px-3 py-2.5 border-b border-gray-100 space-y-2.5">
                <div className="flex items-center gap-2.5">
                  {createdTeamDisplay.logoPath ? (
                    <img
                      src={createdTeamDisplay.logoPath}
                      alt={createdTeamDisplay.name}
                      className="w-9 h-9 rounded-md object-cover flex-shrink-0 border border-gray-100"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-md bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-xs tracking-tight select-none">
                        {teamInitials}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-800 text-sm font-semibold truncate">{createdTeamDisplay.name}</div>
                    <div className="text-gray-500 text-[11px] mt-0.5 truncate">{teamPlanLabel}</div>
                  </div>
                  <span
                    className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${teamSubBadge.className}`}
                  >
                    {teamSubBadge.label}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <i className="ri-calendar-event-line text-gray-400 text-xs flex-shrink-0" />
                    <span className="truncate">{formatTeamExpireSubtitle(teamExpireAt, teamSubStatus)}</span>
                  </div>
                  {teamSeatSummary ? (
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                      <i className="ri-group-line text-gray-400 text-xs flex-shrink-0" />
                      <span className="truncate">{teamSeatSummary}</span>
                    </div>
                  ) : null}
                </div>
                {teamCode ? (
                  <div className="flex items-center gap-2 rounded-md bg-gray-50 border border-gray-100 px-2.5 py-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider">Team code</div>
                      <div className="text-sky-600 font-mono font-semibold text-xs tracking-wider truncate">
                        {teamCode.toUpperCase()}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleCopyTeamCode()}
                      className="flex-shrink-0 h-7 px-2 rounded-md text-[11px] font-medium text-green-700 bg-white border border-green-200 hover:bg-green-50"
                    >
                      {codeCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {group.items.map((item, idx) => (
              <button
                key={item.label}
                onClick={() => item.path && void handleSettingNavigate(item.path)}
                className={`w-full px-3 py-2 flex items-center gap-2.5 cursor-pointer hover:bg-gray-50 transition-colors text-left ${
                  idx < group.items.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 flex-shrink-0">
                  <i className={`${item.icon} text-gray-500 text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-gray-800 text-xs font-medium">{item.label}</div>
                  <div className="text-gray-400 text-[11px] mt-0.5">{item.desc}</div>
                </div>
                <i className="ri-arrow-right-s-line text-gray-300 text-base flex-shrink-0" />
              </button>
            ))}
          </div>
        ))}

        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 flex items-center justify-center rounded-md bg-green-50">
              <i className="ri-information-line text-green-600 text-sm" />
            </div>
            <div>
              <div className="text-gray-800 text-xs font-medium">finfins</div>
              <div className="text-gray-400 text-[11px]">v3.2.4.1029</div>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="w-full bg-white border border-red-100 rounded-lg px-3 py-2.5 flex items-center justify-center gap-2 cursor-pointer hover:bg-red-50 transition-colors"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-logout-box-r-line text-red-500 text-base" />
          </div>
          <span className="text-red-500 font-medium text-xs">Sign Out</span>
        </button>
      </div>

      {showLogoutConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setShowLogoutConfirm(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-xl px-4 pt-4 pb-8"
            style={{ maxWidth: 480, margin: '0 auto' }}
          >
            <div className="w-8 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-red-50 mb-2.5">
                <i className="ri-logout-box-r-line text-red-500 text-lg" />
              </div>
              <div className="text-gray-900 font-semibold text-sm">Sign Out?</div>
              <div className="text-gray-400 text-xs mt-1 leading-relaxed">
                You'll be logged out of your account.<br />Any unsaved progress may be lost.
              </div>
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 font-medium text-xs cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 h-9 rounded-lg bg-red-500 text-white font-medium text-xs cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
